/**
 * FAQ with Image Section
 * Handles accordion functionality with single-open behavior
 * Uses native <details> elements for semantic HTML and accessibility
 */

(function () {
  'use strict';

  // Track initialized sections to prevent duplicate event listeners
  const initializedSections = new WeakSet();

  /**
   * Initialize FAQ accordion behavior
   * Ensures only one FAQ item is open at a time
   */
  const initFaqWithImage = () => {
    const sections = document.querySelectorAll('[id^="faq-with-image-"]');

    sections.forEach((section) => {
      // Skip if already initialized
      if (initializedSections.has(section)) {
        return;
      }

      // Mark as initialized
      initializedSections.add(section);

      // Get section ID and settings
      const sectionId = section.getAttribute('data-section-id');
      const accordion = section.querySelector('.faq-with-image__accordion');
      const media = section.querySelector('.faq-with-image__media');

      if (!accordion) {
        return;
      }

      // Apply image settings as CSS variables
      applyImageSettings(section, sectionId);

      // Get all details elements
      const detailsElements = accordion.querySelectorAll('details');

      // Single-open accordion behavior using event delegation
      accordion.addEventListener('toggle', (event) => {
        const openedDetails = event.target;

        // Only proceed if a details element was opened (not closed)
        if (openedDetails.tagName === 'DETAILS' && openedDetails.open) {
          // Close all other details elements
          detailsElements.forEach((details) => {
            if (details !== openedDetails && details.open) {
              details.open = false;
            }
          });
        }
      }, true); // Use capture phase to catch all toggle events

      // Keyboard navigation enhancement
      enhanceKeyboardNavigation(accordion, detailsElements);
    });
  };

  /**
   * Apply image settings as CSS custom properties
   * @param {HTMLElement} section - The section element
   * @param {string} sectionId - The section ID
   */
  const applyImageSettings = (section, sectionId) => {
    const media = section.querySelector('.faq-with-image__media');
    if (!media) return;

    // Get settings from schema (passed via inline style in Liquid)
    const computedStyle = getComputedStyle(section);

    // Image height setting
    const imageHeight = section.dataset.imageHeight || '80vh';
    media.style.setProperty('--faq-image-height', imageHeight);

    // Image position setting
    const imagePosition = section.dataset.imagePosition || 'center top';
    const img = media.querySelector('.faq-with-image__img');
    if (img) {
      img.style.objectPosition = imagePosition;
    }
  };

  /**
   * Enhance keyboard navigation for accessibility
   * Arrow keys to move between FAQ items
   * @param {HTMLElement} accordion - The accordion container
   * @param {NodeList} detailsElements - All details elements
   */
  const enhanceKeyboardNavigation = (accordion, detailsElements) => {
    const summaries = Array.from(detailsElements).map(d => d.querySelector('summary'));

    summaries.forEach((summary, index) => {
      summary.addEventListener('keydown', (event) => {
        let targetIndex;

        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            targetIndex = (index + 1) % summaries.length;
            summaries[targetIndex].focus();
            break;

          case 'ArrowUp':
            event.preventDefault();
            targetIndex = (index - 1 + summaries.length) % summaries.length;
            summaries[targetIndex].focus();
            break;

          case 'Home':
            event.preventDefault();
            summaries[0].focus();
            break;

          case 'End':
            event.preventDefault();
            summaries[summaries.length - 1].focus();
            break;
        }
      });
    });
  };

  /**
   * Initialize on DOM ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFaqWithImage);
  } else {
    initFaqWithImage();
  }

  /**
   * Re-initialize when sections are loaded/reloaded in theme editor
   */
  document.addEventListener('shopify:section:load', initFaqWithImage);
})();
